/**
 * Accueil (design system, EcranPaquets) : paquets principaux et sous-paquets, cartes du jour,
 * durée estimée et « Lancer la séance ».
 */
import { useState, type KeyboardEvent } from 'react';
import { Bouton } from '../composants/Bouton';
import { EtatVide } from '../composants/EtatVide';
import { Icone } from '../composants/Icone';
import { COMPTEURS_VIDES, type Compteurs } from '../../seance/compteurs';
import { Coquille } from '../Coquille';
import { aplatir, indexer, type NoeudPaquet } from '../donnees/arbre';
import { useDonnees } from '../donnees/contexte';
import type { Ancre } from '../composants/Menu';
import { naviguer } from '../routes';
import { cx, nombre, pluriel } from '../outils';
import { decrireEcheance, dureeCourte } from '../format';

/** Libellés des compteurs, affichés à côté des nombres quand les en-têtes de colonne disparaissent. */
/** Libellé affiché après le nombre sur téléphone (« 1 nouvelle », « 2 nouvelles »). */
function libelleCompteur(cle: 'nouvelles' | 'apprentissage' | 'aRevoir', n: number): string {
  if (cle === 'nouvelles') return n > 1 ? 'nouvelles' : 'nouvelle';
  return cle === 'apprentissage' ? 'en apprentissage' : 'à revoir';
}
import { nomCompteurs } from '../composants/ArbreDesPaquets';

export interface EcranPaquetsProps {
  selection: string | null;
  deplies: ReadonlySet<string>;
  onDeplier: (id: string, deplie: boolean) => void;
  noeuds: NoeudPaquet[];
  menuOuvert: string | null;
  onActions: (id: string, ancre: Ancre) => void;
  onNouveauPaquet: () => void;
}

export function EcranPaquets({
  selection,
  deplies,
  onDeplier,
  noeuds,
  menuOuvert,
  onActions,
  onNouveauPaquet,
}: EcranPaquetsProps) {
  const { paquets, jours } = useDonnees();
  const index = indexer(noeuds);
  const [actif, setActif] = useState<string | null>(selection);
  const choisi = actif ?? selection;

  const visibles = aplatir(noeuds).filter((n) => {
    let parent = n.paquet.parentId;
    while (parent) {
      if (!deplies.has(parent)) return false;
      parent = index.get(parent)?.paquet.parentId ?? null;
    }
    return true;
  });

  const compteurs = (n: NoeudPaquet): Compteurs =>
    jours.get(n.principalId)?.parPaquet.get(n.paquet.id) ?? COMPTEURS_VIDES;
  const principaux = noeuds.map((n) => ({ n, jour: jours.get(n.paquet.id) }));
  const total = principaux.reduce((s, { jour }) => s + (jour?.total ?? 0), 0);
  const avecCartes = principaux.filter(({ jour }) => (jour?.total ?? 0) > 0).length;
  const duree = principaux.reduce((s, { jour }) => s + (jour?.dureeEstimee ?? 0), 0);
  const prochaine = principaux
    .map(({ jour }) => jour?.prochaineEcheance)
    .filter((d): d is Date => d instanceof Date)
    .sort((a, b) => a.getTime() - b.getTime())[0];

  let resume: string | null = null;
  if (paquets !== null && noeuds.length > 0 && jours.size > 0) {
    resume =
      total === 0
        ? `Rien à réviser.${prochaine ? ` Prochaine carte due ${decrireEcheance(prochaine)}.` : ''}`
        : `${pluriel(total, 'carte')} à réviser dans ${pluriel(avecCartes, 'paquet')}${
            duree > 0 ? `, environ ${dureeCourte(duree)}` : ''
          }.`;
  }

  function clavier(e: KeyboardEvent<HTMLDivElement>, n: NoeudPaquet) {
    if (e.target !== e.currentTarget) return;
    const i = visibles.indexOf(n);
    const aller = (cible: NoeudPaquet | undefined) => {
      if (!cible) return;
      setActif(cible.paquet.id);
      document.querySelector<HTMLElement>(`[data-ligne="${CSS.escape(cible.paquet.id)}"]`)?.focus();
    };
    switch (e.key) {
      case 'ArrowDown':
        aller(visibles[i + 1]);
        break;
      case 'ArrowUp':
        aller(visibles[i - 1]);
        break;
      case 'Home':
        aller(visibles[0]);
        break;
      case 'End':
        aller(visibles.at(-1));
        break;
      case 'ArrowRight':
        if (n.enfants.length === 0) return;
        if (!deplies.has(n.paquet.id)) onDeplier(n.paquet.id, true);
        else aller(n.enfants[0]);
        break;
      case 'ArrowLeft':
        if (deplies.has(n.paquet.id) && n.enfants.length > 0) onDeplier(n.paquet.id, false);
        else aller(n.paquet.parentId ? index.get(n.paquet.parentId) : undefined);
        break;
      case 'Enter':
      case ' ':
        naviguer({ ecran: 'seance', paquet: n.principalId });
        break;
      case 'F10':
        if (!e.shiftKey) return;
        onActions(n.paquet.id, e.currentTarget);
        break;
      case 'ContextMenu':
        onActions(n.paquet.id, e.currentTarget);
        break;
      default:
        return;
    }
    e.preventDefault();
  }

  // Recherche et « Nouveau paquet » doublent la barre latérale : seulement quand elle est repliée.
  const actions = (
    <>
      <Bouton
        variante="discret"
        taille="compacte"
        iconeSeule
        icone="search"
        etiquette="Rechercher une carte"
        raccourci="/"
        className="fc-seulement-sans-laterale"
        onClick={() => naviguer({ ecran: 'gestion' })}
      />
      {noeuds.length > 0 && (
        <Bouton
          variante="discret"
          taille="compacte"
          iconeSeule
          icone="folder-plus"
          etiquette="Nouveau paquet"
          className="fc-seulement-sans-laterale"
          onClick={onNouveauPaquet}
        />
      )}
    </>
  );

  return (
    <Coquille titre="Paquets" actions={actions} colonne="large">
      <div className="fc-fenetre__entete">
        <h2 className="fc-fenetre__titre-ecran">Aujourd’hui</h2>
      </div>
      {paquets !== null && noeuds.length === 0 ? (
        <EtatVide
          icone="layers"
          titre="Aucun paquet"
          texte="Créez un paquet principal pour ranger vos premières cartes."
          action={
            <Bouton variante="principal" icone="folder-plus" onClick={onNouveauPaquet}>
              Créer un paquet
            </Bouton>
          }
          style={{ paddingTop: 'var(--espace-48)' }}
        />
      ) : (
        <>
          {resume && <p className="fc-ecran-paquets__resume">{resume}</p>}
          <div
            className="fc-ecran-paquets__liste"
            role="treegrid"
            aria-label="Paquets"
            aria-readonly="true"
          >
            <div className="fc-ecran-paquets__entete" role="row">
              <span role="columnheader">Paquet</span>
              <span className="fc-ecran-paquets__cellule--nombre" role="columnheader">
                Nouvelles
              </span>
              <span
                className="fc-ecran-paquets__cellule--nombre"
                role="columnheader"
                title="En apprentissage"
              >
                Apprentissage
              </span>
              <span className="fc-ecran-paquets__cellule--nombre" role="columnheader">
                À revoir
              </span>
              <span
                className="fc-ecran-paquets__cellule--duree"
                role="columnheader"
                aria-label="Durée estimée"
                title="Durée estimée"
              >
                Durée
              </span>
              <span role="columnheader">
                <span className="fc-sr">Actions</span>
              </span>
            </div>
            {visibles.map((n, i) => {
              const id = n.paquet.id;
              const c = compteurs(n);
              const principal = n.niveau === 0;
              const selectionne = choisi === id;
              const dureeEstimee = principal ? jours.get(id)?.dureeEstimee : null;
              const tabulable = choisi
                ? selectionne || (!visibles.some((v) => v.paquet.id === choisi) && i === 0)
                : i === 0;
              return (
                <div
                  key={id}
                  data-ligne={id}
                  className="fc-ecran-paquets__ligne"
                  role="row"
                  aria-level={n.niveau + 1}
                  aria-expanded={n.enfants.length > 0 ? deplies.has(id) : undefined}
                  aria-selected={selectionne}
                  tabIndex={tabulable ? 0 : -1}
                  style={{ ['--niveau' as string]: n.niveau }}
                  aria-label={`${n.paquet.nom}, ${nomCompteurs(c)}`}
                  onClick={() => setActif(id)}
                  onDoubleClick={() => naviguer({ ecran: 'seance', paquet: n.principalId })}
                  onKeyDown={(e) => clavier(e, n)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    onActions(id, { x: e.clientX, y: e.clientY });
                  }}
                >
                  <span className="fc-ecran-paquets__nom" role="gridcell">
                    {n.enfants.length > 0 ? (
                      <button
                        type="button"
                        className="fc-ecran-paquets__chevron"
                        tabIndex={-1}
                        aria-label={`${deplies.has(id) ? 'Replier' : 'Déplier'} ${n.paquet.nom}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeplier(id, !deplies.has(id));
                        }}
                      >
                        <Icone nom="chevron-right" />
                      </button>
                    ) : (
                      <span
                        className="fc-ecran-paquets__chevron fc-ecran-paquets__chevron--vide"
                        aria-hidden="true"
                      />
                    )}
                    <Icone nom={principal ? 'layers' : 'folder'} />
                    <span className="fc-ecran-paquets__libelle" title={n.paquet.nom}>
                      {n.paquet.nom}
                    </span>
                  </span>
                  {(['nouvelles', 'apprentissage', 'aRevoir'] as const).map((cle) => (
                    <span
                      key={cle}
                      className={cx(
                        'fc-ecran-paquets__cellule--nombre',
                        `fc-ecran-paquets__nombre--${cle === 'aRevoir' ? 'a-revoir' : cle}`,
                      )}
                      role="gridcell"
                      data-zero={c[cle] === 0 ? '' : undefined}
                      data-libelle={libelleCompteur(cle, c[cle])}
                    >
                      {nombre(c[cle])}
                    </span>
                  ))}
                  <span className="fc-ecran-paquets__cellule--duree" role="gridcell">
                    {dureeEstimee ? `≈ ${dureeCourte(dureeEstimee)}` : ''}
                  </span>
                  <span className="fc-ecran-paquets__actions" role="gridcell">
                    {principal &&
                      (jours.get(id)?.nombreCartes === 0 ? (
                        <Bouton
                          variante={selectionne ? 'principal' : 'secondaire'}
                          taille="compacte"
                          icone="plus"
                          className="fc-ecran-paquets__lancer"
                          tabIndex={-1}
                          onClick={(e) => {
                            e.stopPropagation();
                            naviguer({ ecran: 'editeur', paquet: id });
                          }}
                        >
                          Ajouter une carte
                        </Bouton>
                      ) : (
                        <Bouton
                          variante={selectionne ? 'principal' : 'secondaire'}
                          taille="compacte"
                          icone="play"
                          className="fc-ecran-paquets__lancer"
                          tabIndex={-1}
                          onClick={(e) => {
                            e.stopPropagation();
                            naviguer({ ecran: 'seance', paquet: id });
                          }}
                        >
                          Lancer la séance
                        </Bouton>
                      ))}
                    <button
                      type="button"
                      className="fc-bouton fc-bouton--discret fc-bouton--compact fc-bouton--icone fc-ecran-paquets__plus"
                      tabIndex={-1}
                      aria-label={`Plus d’actions sur ${n.paquet.nom}`}
                      title="Plus d’actions"
                      aria-haspopup="menu"
                      aria-expanded={menuOuvert === id}
                      onClick={(e) => {
                        e.stopPropagation();
                        onActions(id, e.currentTarget);
                      }}
                    >
                      <Icone nom="ellipsis" />
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </Coquille>
  );
}
